import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Native wishlist: hearts on product cards + product page, backed by
 * localStorage for guests and the wishlist Worker (via App Proxy) for
 * logged-in customers. See snippets/wishlist-heart-button.liquid for the
 * markup and the plan doc for the overall architecture.
 */

const GUEST_KEY = 'pbw_wishlist';
const PROXY_PATH = '/apps/wishlist-1';

/** @returns {string[]} */
function getGuestWishlist() {
  try {
    const parsed = JSON.parse(localStorage.getItem(GUEST_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** @param {string[]} ids */
function setGuestWishlist(ids) {
  localStorage.setItem(GUEST_KEY, JSON.stringify(ids));
}

function isLoggedIn() {
  return Boolean(window.__wishlistCustomerId);
}

/** @param {string[]} ids */
function dispatchWishlistChange(ids) {
  document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { productIds: ids } }));
}

/** Cache of the logged-in customer's wishlist, populated on first load/merge. */
let accountIdsCache = null;

async function fetchAccountWishlist() {
  const res = await fetch(PROXY_PATH, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`wishlist fetch failed: ${res.status}`);
  const data = await res.json();
  accountIdsCache = data.productIds ?? [];
  return data;
}

/** @param {string} productId */
async function toggleAccountWishlist(productId) {
  const res = await fetch(`${PROXY_PATH}/toggle`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ productId }),
  });
  if (!res.ok) throw new Error(`wishlist toggle failed: ${res.status}`);
  const data = await res.json();
  accountIdsCache = data.productIds ?? [];
  return data;
}

/** @param {string[]} productIds */
async function mergeGuestWishlist(productIds) {
  const res = await fetch(`${PROXY_PATH}/merge`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ productIds }),
  });
  if (!res.ok) throw new Error(`wishlist merge failed: ${res.status}`);
  const data = await res.json();
  accountIdsCache = data.productIds ?? [];
  return data;
}

/**
 * Resolves guest product IDs (from localStorage) to display data via the
 * Worker's /resolve route. This route needs no login — it just proxies to
 * the Storefront API — but still requires a valid App Proxy signature, so
 * the Storefront token itself never has to reach the browser.
 * @param {string[]} productIds
 */
async function resolveGuestProducts(productIds) {
  if (productIds.length === 0) return [];
  const params = new URLSearchParams({ ids: productIds.join(',') });
  const res = await fetch(`${PROXY_PATH}/resolve?${params}`, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`wishlist resolve failed: ${res.status}`);
  const data = await res.json();
  return data.products ?? [];
}

/**
 * @typedef {Object} WishlistHeartRefs
 * @property {HTMLButtonElement} button
 */

/** @extends {Component<WishlistHeartRefs>} */
class WishlistHeartComponent extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();
    this.#syncActiveState();
    document.addEventListener('wishlist:change', this.#onWishlistChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('wishlist:change', this.#onWishlistChange);
  }

  get productId() {
    return this.dataset.productId ?? '';
  }

  #onWishlistChange = () => this.#syncActiveState();

  #currentIds() {
    if (isLoggedIn()) return accountIdsCache ?? [];
    return getGuestWishlist();
  }

  #syncActiveState() {
    const active = this.#currentIds().includes(this.productId);
    this.toggleAttribute('data-active', active);
    this.refs.button.setAttribute('aria-pressed', String(active));
  }

  async toggle() {
    const { productId } = this;
    if (!productId) return;

    const wasActive = this.hasAttribute('data-active');

    // Optimistic UI — reverted below if the account write fails.
    this.toggleAttribute('data-active', !wasActive);
    this.refs.button.setAttribute('aria-pressed', String(!wasActive));

    if (isLoggedIn()) {
      try {
        const { productIds } = await toggleAccountWishlist(productId);
        dispatchWishlistChange(productIds);
      } catch (error) {
        console.error('[wishlist] toggle failed', error);
        this.toggleAttribute('data-active', wasActive);
        this.refs.button.setAttribute('aria-pressed', String(wasActive));
      }
      return;
    }

    const current = getGuestWishlist();
    const next = wasActive ? current.filter((id) => id !== productId) : [...current, productId];
    setGuestWishlist(next);
    dispatchWishlistChange(next);
  }
}

if (!customElements.get('wishlist-heart-component')) {
  customElements.define('wishlist-heart-component', WishlistHeartComponent);
}

/**
 * Keeps the header icon's count badge in sync. Matches the cart bubble's own
 * pattern (hidden entirely at zero, solid colour otherwise) instead of the
 * old app's washed-out "empty" colour (assets/base.css had a
 * `[data-total="0"] { background: #d8bbc4 }` rule for that faded look).
 */
function syncHeaderBadge() {
  const count = (isLoggedIn() ? accountIdsCache ?? [] : getGuestWishlist()).length;

  document.querySelectorAll('.wishlist-total-count').forEach((el) => {
    el.textContent = String(count);
    el.dataset.total = String(count);
    el.classList.toggle('wishlist-h-count--empty', count === 0);
  });
}

document.addEventListener('wishlist:change', syncHeaderBadge);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncHeaderBadge);
} else {
  syncHeaderBadge();
}

/**
 * On first load after login, fold any guest-collected wishlist items into
 * the account's list, then clear the local copy — the account becomes the
 * single source of truth from that point on. Also primes accountIdsCache so
 * hearts render correctly without waiting on a click.
 */
async function initAccountState() {
  if (!isLoggedIn()) return;

  const guestIds = getGuestWishlist();
  try {
    if (guestIds.length > 0) {
      const { productIds } = await mergeGuestWishlist(guestIds);
      localStorage.removeItem(GUEST_KEY);
      dispatchWishlistChange(productIds);
    } else {
      const { productIds } = await fetchAccountWishlist();
      dispatchWishlistChange(productIds);
    }
  } catch (error) {
    console.error('[wishlist] account init failed', error);
  }
}

/**
 * The product page has an existing "Heart" slot (built by a previous
 * developer) sitting in the Type/Region/Stock row, containing a raw
 * `<button class="wishlist-engine" data-product_id="...">` placeholder that
 * se-wishlist-engine's own script used to populate. Now that app is
 * disabled, the placeholder sits empty in the DOM. Rather than editing the
 * (very large) product template JSON to swap that block's content, upgrade
 * the placeholder in place — same slot, same row, no template changes.
 */
function upgradeLegacyWishlistPlaceholders() {
  const placeholders = document.querySelectorAll('button.wishlist-engine[data-product_id]');

  placeholders.forEach((placeholder) => {
    const productId = placeholder.dataset.product_id;
    if (!productId) return;

    const wrapper = document.createElement('wishlist-heart-component');
    wrapper.className = 'wishlist-heart';
    wrapper.dataset.productId = productId;
    wrapper.style.setProperty('--wishlist-heart-color', '#7A283F');
    wrapper.innerHTML = `
      <button type="button" class="wishlist-heart__button" ref="button" on:click="/toggle" aria-pressed="false" aria-label="Add to wishlist">
        <span class="svg-wrapper wishlist-heart__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
          </svg>
        </span>
      </button>
    `;

    placeholder.replaceWith(wrapper);
  });
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]
  ));
}

function formatMoney(amount, currencyCode) {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currencyCode }).format(Number(amount));
  } catch {
    return `${amount} ${currencyCode}`;
  }
}

const wishlistPageEl = () => document.querySelector('.wishlist-page');

/** @param {object} product Storefront API product node (see resolveProducts in the Worker) */
function renderWishlistCardHTML(product) {
  const price = product.priceRange?.minVariantPrice;
  const compareAt = product.compareAtPriceRange?.minVariantPrice;
  const onSale = compareAt && Number(compareAt.amount) > Number(price?.amount ?? 0);
  const image = product.featuredImage;
  const soldOut = product.availableForSale === false;

  let priceHTML = '';
  if (soldOut) {
    priceHTML = '<div class="wishlist-page-card__sold-out">Sold out</div>';
  } else if (price) {
    const compareHTML = onSale
      ? `<span class="compare-at-price">${escapeHtml(formatMoney(compareAt.amount, compareAt.currencyCode))}</span>`
      : '';
    priceHTML = `<div class="wishlist-page-card__price">${compareHTML}${escapeHtml(formatMoney(price.amount, price.currencyCode))}</div>`;
  }

  return `
    <li class="wishlist-page-card" data-product-id="${escapeHtml(product.id)}">
      <a class="wishlist-page-card__link" href="/products/${escapeHtml(product.handle)}" aria-label="${escapeHtml(product.title)}"></a>
      <div class="wishlist-page-card__media">
        ${image ? `<img class="wishlist-page-card__media-img" src="${escapeHtml(image.url)}" alt="${escapeHtml(image.altText || product.title)}" loading="lazy" width="400" height="533">` : ''}
        ${product.hoverImage ? `<img class="wishlist-page-card__media-img wishlist-page-card__media-img--hover" src="${escapeHtml(product.hoverImage)}" alt="" loading="lazy" width="400" height="533">` : ''}
        <wishlist-heart-component class="wishlist-heart" data-product-id="${escapeHtml(product.id)}" style="--wishlist-heart-color: #7A283F;">
          <button type="button" class="wishlist-heart__button" ref="button" on:click="/toggle" aria-pressed="false" aria-label="Remove from wishlist">
            <span class="svg-wrapper wishlist-heart__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
              </svg>
            </span>
          </button>
        </wishlist-heart-component>
      </div>
      ${product.productType ? `<div class="wishlist-page-card__type">${escapeHtml(product.productType)}</div>` : ''}
      <h3 class="wishlist-page-card__title">${escapeHtml(product.title)}</h3>
      ${
        product.subName || product.alcohol
          ? `<div class="wishlist-page-card__meta"><span>${escapeHtml(product.subName ?? '')}</span>${product.alcohol ? `<span>${escapeHtml(product.alcohol)}</span>` : ''}</div>`
          : ''
      }
      ${priceHTML}
      ${
        product.variantId && product.variantAvailableForSale !== false
          ? `<div class="wishlist-page-card__actions">${
              wishlistPageEl()?.dataset.showQty === 'false'
                ? ''
                : `<div class="wishlist-page-card__qty"><button type="button" class="wishlist-page-card__step" data-step="-1" aria-label="Decrease quantity">&minus;</button><input type="number" class="wishlist-page-card__qty-input" value="1" min="1" step="1" inputmode="numeric" aria-label="Quantity"><button type="button" class="wishlist-page-card__step" data-step="1" aria-label="Increase quantity">+</button></div>`
            }<button type="button" class="button wishlist-page-card__add-to-cart" data-variant-id="${escapeHtml(product.variantId)}" data-product-id="${escapeHtml(product.id)}">${escapeHtml(wishlistPageEl()?.dataset.atcLabel || 'Add to Basket')}</button></div>`
          : ''
      }
    </li>
  `;
}

/**
 * "Add to Basket" adds the variant to the real cart and leaves the item on
 * the wishlist (the customer may want it again later). The header cart
 * badge is updated by dispatching the theme's own CartLinesUpdateEvent —
 * the same event assets/product-form.js fires on a normal add-to-cart —
 * since cart-icon.js only listens for that specific event with a
 * `.promise` resolving to `{cart, detail}`; a plain CustomEvent does
 * nothing, which is why the badge previously only updated after a reload.
 * @param {string} variantId
 * @param {string} productId
 */
async function addWishlistCardToBasket(variantId, button, quantity = 1) {
  button.disabled = true;
  const originalText = button.textContent;
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'wishlist',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
    });
    if (!res.ok) throw new Error(`cart add failed: ${res.status}`);

    const ajaxCart = await fetch('/cart.js', { headers: { accept: 'application/json' } }).then((r) => r.json());

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: { items: ajaxCart.items, source: 'wishlist-page', itemCount: quantity },
    });

    button.textContent = wishlistPageEl()?.dataset.addedLabel || 'Added ✓';
    setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
    }, 1500);
  } catch (error) {
    console.error('[wishlist] add to basket failed', error);
    deferred.reject(error);
    button.textContent = 'Try again';
    button.disabled = false;
    setTimeout(() => {
      button.textContent = originalText;
    }, 1800);
  }
}

document.addEventListener('click', (event) => {
  const step = event.target.closest('.wishlist-page-card__step');
  if (step) {
    const input = step.parentNode.querySelector('input');
    if (step.dataset.step === '1') input.stepUp();
    else input.stepDown();
    return;
  }
  const button = event.target.closest('.wishlist-page-card__add-to-cart');
  if (!button) return;
  const input = button.closest('.wishlist-page-card')?.querySelector('.wishlist-page-card__qty-input');
  const quantity = Math.max(1, parseInt(input?.value, 10) || 1);
  addWishlistCardToBasket(button.dataset.variantId, button, quantity);
});

/**
 * Populates the dedicated wishlist page (sections/wishlist-page.liquid).
 * No-ops on every other page — the root element only exists there.
 */
async function renderWishlistPage() {
  const root = document.querySelector('[data-wishlist-page-root]');
  if (!root) return;

  const loadingEl = root.querySelector('[data-wishlist-loading]');
  const emptyEl = root.querySelector('[data-wishlist-empty]');
  const gridEl = root.querySelector('[data-wishlist-grid]');
  const guestNoticeEl = document.querySelector('[data-wishlist-guest-notice]');
  const headingEl = document.querySelector('[data-wishlist-heading]');
  const subheadingEl = document.querySelector('[data-wishlist-subheading]');

  let products = [];
  try {
    if (isLoggedIn()) {
      const data = await fetchAccountWishlist();
      products = data.products ?? [];
    } else {
      products = await resolveGuestProducts(getGuestWishlist());
    }
  } catch (error) {
    console.error('[wishlist] page load failed', error);
  }

  // The section's own `.wishlist-page__loading { display: flex; ... }` rule
  // is author-origin CSS, which always wins over the browser's default
  // `[hidden] { display: none }` UA-stylesheet rule regardless of
  // specificity — so setting the `hidden` attribute alone did nothing
  // visually here. An inline style always beats an external stylesheet
  // rule that isn't `!important`, so set display directly instead.
  if (loadingEl) loadingEl.style.display = 'none';

  if (products.length === 0) {
    // An empty wishlist should show nothing but the empty-state message
    // itself — the page heading/subheading and the "you're viewing as a
    // guest" notice are all real content promising a wishlist that, in this
    // case, doesn't exist yet, which just reads as clutter/noise above the
    // one message that actually matters.
    if (headingEl) headingEl.style.display = 'none';
    if (subheadingEl) subheadingEl.style.display = 'none';
    if (guestNoticeEl) guestNoticeEl.style.display = 'none';
    if (emptyEl) emptyEl.hidden = false;
    return;
  }

  // initAccountState() dispatches wishlist:change (with the account's real
  // product ids) before this function has put any cards in the grid — at
  // that instant the wishlist:change listener below sees an empty grid and
  // reveals emptyEl, assuming the wishlist itself is empty. That guess is
  // wrong whenever there ARE products (this is just it, and it hasn't
  // rendered them yet), so once real products are confirmed here, undo it
  // (and the heading/subheading/guest-notice hides that would have come
  // with it).
  if (emptyEl) emptyEl.hidden = true;
  if (headingEl) headingEl.style.display = '';
  if (subheadingEl) subheadingEl.style.display = '';
  if (guestNoticeEl) guestNoticeEl.style.display = '';

  if (gridEl) {
    gridEl.hidden = false;
    gridEl.innerHTML = products.map(renderWishlistCardHTML).join('');
  }
}

/** Removes a card from the wishlist page the moment its heart is un-toggled. */
document.addEventListener('wishlist:change', (event) => {
  const gridEl = document.querySelector('[data-wishlist-grid]');
  if (!gridEl) return;

  const currentIds = new Set(event.detail.productIds);
  gridEl.querySelectorAll('.wishlist-page-card').forEach((card) => {
    if (!currentIds.has(card.dataset.productId)) card.remove();
  });

  if (gridEl.children.length === 0) {
    gridEl.hidden = true;
    const emptyEl = document.querySelector('[data-wishlist-empty]');
    if (emptyEl) emptyEl.hidden = false;
    const guestNoticeEl = document.querySelector('[data-wishlist-guest-notice]');
    if (guestNoticeEl) guestNoticeEl.style.display = 'none';
    const headingEl = document.querySelector('[data-wishlist-heading]');
    if (headingEl) headingEl.style.display = 'none';
    const subheadingEl = document.querySelector('[data-wishlist-subheading]');
    if (subheadingEl) subheadingEl.style.display = 'none';
  }
});

async function boot() {
  upgradeLegacyWishlistPlaceholders();
  await initAccountState();
  renderWishlistPage();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}


// Theme editor: changing a setting re-draws the section without reloading the
// page, so fill the cards again straight away instead of waiting for a save.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-wishlist-page-root]')) renderWishlistPage();
});
